// Client-side list query helpers (00-ds-09, D148). In production the server
// does search/sort/pagination (§8); for the demo everything runs in the browser
// over mock data. The "now" the presets are measured from is the mock date so
// the fixtures line up with the rest of staging (Oct 2026).

import type { HistoryDatePreset } from './datePresets';

/** The mock "today" the whole RCM demo is dated around. */
export const MOCK_NOW = new Date('2026-10-16T12:00:00');

export interface DateRange {
  /** Inclusive start (ISO yyyy-mm-dd) or null for open-ended. */
  from: string | null;
  to: string | null;
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** The {from,to} window for a preset, measured from MOCK_NOW. `all-time` → nulls. */
export function rangeForPreset(preset: HistoryDatePreset, custom?: DateRange): DateRange {
  const now = MOCK_NOW;
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case 'this-month':
      return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
    case 'last-month':
      return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    case 'last-90': {
      const d = new Date(now);
      d.setDate(d.getDate() - 90);
      return { from: iso(d), to: iso(now) };
    }
    case 'this-year':
      return { from: iso(new Date(y, 0, 1)), to: iso(now) };
    case 'all-time':
      return { from: null, to: null };
    case 'custom':
      return custom ?? { from: null, to: null };
  }
}

/** A short human label for a preset window, for a band/filter line ("October 2026"). */
export function windowLabel(preset: HistoryDatePreset, custom?: DateRange): string {
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const now = MOCK_NOW;
  switch (preset) {
    case 'this-month':
      return `${MONTHS[now.getMonth()] ?? ''} ${String(now.getFullYear())}`;
    case 'last-month': {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return `${MONTHS[d.getMonth()] ?? ''} ${String(d.getFullYear())}`;
    }
    case 'last-90':
      return 'the last 90 days';
    case 'this-year':
      return String(now.getFullYear());
    case 'all-time':
      return 'all time';
    case 'custom':
      return custom?.from && custom.to ? `${custom.from} – ${custom.to}` : 'the selected range';
  }
}

/** True when `date` (ISO yyyy-mm-dd) falls inside the range (nulls = open). */
export function inRange(date: string, range: DateRange): boolean {
  if (range.from && date < range.from) return false;
  if (range.to && date > range.to) return false;
  return true;
}

/** Case-insensitive substring match across the strings `textOf` returns. */
export function applySearch<T>(rows: T[], query: string, textOf: (r: T) => string): T[] {
  const q = query.trim().toLowerCase();
  if (q === '') return rows;
  return rows.filter((r) => textOf(r).toLowerCase().includes(q));
}

/** Stable sort by a key extractor; `dir` -1 = descending (newest/largest first). */
export function sortBy<T>(rows: T[], keyOf: (r: T) => number | string, dir: 1 | -1 = -1): T[] {
  return [...rows].sort((a, b) => {
    const ka = keyOf(a);
    const kb = keyOf(b);
    if (ka < kb) return -1 * dir;
    if (ka > kb) return 1 * dir;
    return 0;
  });
}

/** One page of rows (1-based page). */
export function paginate<T>(rows: T[], page: number, pageSize = 50): T[] {
  const start = (Math.max(1, page) - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

/** The first `count` rows — the load-more model for queues. */
export function takeLoaded<T>(rows: T[], loaded: number): T[] {
  return rows.slice(0, Math.max(0, loaded));
}
